import {
  PDI_ADVERSE,
  type PdiCheckpointStatus,
  type PdiTreeArea,
  type PdiTreeCheckpoint,
  pdiCheckpointStatus,
} from '@felix/domain';
import type { StatusTone } from '../../StatusBadge.js';
import type {
  DrydockProject,
  PdiApplicabilityRow,
  PdiInspectionSummary,
  PdiResultRow,
} from './types.js';

/**
 * Pre-Docking denetim ekranının saf yardımcıları (ADR 0129 Ek Faz 2): son revizyon, nokta durumu,
 * süzgeç (All / Remaining / Adverse / After Docking) ve denetim seçimi.
 */

export const INSPECTION_FILTERS = ['all', 'remaining', 'adverse', 'after_docking'] as const;
export type InspectionFilter = (typeof INSPECTION_FILTERS)[number];

export const STATUS_TONE: Record<PdiCheckpointStatus, StatusTone> = {
  satisfactory: 'success',
  attention_required: 'warning',
  unsatisfactory: 'danger',
  not_applicable: 'neutral',
  deferred: 'info',
  not_inspected: 'neutral',
  vessel_na: 'neutral',
};

/** Her noktanın geçerli (yerini başkası almamış) revizyonu. */
export function latestByCode(results: readonly PdiResultRow[]): Map<string, PdiResultRow> {
  const map = new Map<string, PdiResultRow>();
  for (const r of results) {
    const seen = map.get(r.checkpointCode);
    if (!seen || r.revision > seen.revision) map.set(r.checkpointCode, r);
  }
  return map;
}

/** Noktanın bütün revizyonları, yeniden eskiye. */
export function revisionsOf(results: readonly PdiResultRow[], code: string): PdiResultRow[] {
  return results.filter((r) => r.checkpointCode === code).sort((a, b) => b.revision - a.revision);
}

/** Gemi profilinde N/A olan noktalar (sayım dışı, sonuç almaz). */
export function vesselNotApplicable(rows: readonly PdiApplicabilityRow[]): Set<string> {
  return new Set(
    rows.filter((r) => r.applicability === 'not_applicable').map((r) => r.checkpointCode),
  );
}

export function statusOf(
  code: string,
  latest: ReadonlyMap<string, PdiResultRow>,
  na: ReadonlySet<string>,
): PdiCheckpointStatus {
  return pdiCheckpointStatus(latest.get(code)?.result, na.has(code));
}

/**
 * Süzgece uyan nokta: Remaining sonucu olmayan ya da ertelenen; After Docking ertelenen ya da
 * önceki denetimlerde ertelenmiş olan (kuyruk, Faz 4 R5).
 */
export function matchesFilter(
  status: PdiCheckpointStatus,
  filter: InspectionFilter,
  queued = false,
): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'remaining':
      return status === 'not_inspected' || status === 'deferred';
    case 'adverse':
      return (PDI_ADVERSE as readonly string[]).includes(status);
    case 'after_docking':
      return status === 'deferred' || (queued && status !== 'vessel_na');
  }
}

export interface CheckpointRow {
  checkpoint: PdiTreeCheckpoint;
  areaName: string;
  systemName: string;
  status: PdiCheckpointStatus;
  latest: PdiResultRow | null;
}

/**
 * Seçili alan ya da sistemin noktaları; süzgeç ve arama (kimlik, ad, kapsam; büyük/küçük harf
 * duyarsız). Ağaç sırası korunur.
 */
export function checkpointRows(
  tree: readonly PdiTreeArea[],
  input: {
    latest: ReadonlyMap<string, PdiResultRow>;
    na: ReadonlySet<string>;
    selected: string | null;
    filter: InspectionFilter;
    query: string;
    /** After Docking kuyruğu (önceki denetimlerde ertelenen noktalar). */
    queue?: ReadonlySet<string>;
  },
): CheckpointRow[] {
  const query = input.query.trim().toLocaleLowerCase();
  const rows: CheckpointRow[] = [];
  for (const area of tree) {
    for (const system of area.systems) {
      for (const checkpoint of system.checkpoints) {
        if (input.selected !== null && !checkpoint.code.startsWith(`${input.selected}-`)) continue;
        const status = statusOf(checkpoint.code, input.latest, input.na);
        if (!matchesFilter(status, input.filter, input.queue?.has(checkpoint.code) ?? false)) {
          continue;
        }
        if (
          query !== '' &&
          !checkpoint.code.toLocaleLowerCase().includes(query) &&
          !checkpoint.name.toLocaleLowerCase().includes(query) &&
          !checkpoint.content.scope.toLocaleLowerCase().includes(query)
        ) {
          continue;
        }
        rows.push({
          checkpoint,
          areaName: area.name,
          systemName: system.name,
          status,
          latest: input.latest.get(checkpoint.code) ?? null,
        });
      }
    }
  }
  return rows;
}

export interface InspectionOption {
  inspection: PdiInspectionSummary;
  project: DrydockProject;
}

/** Seçicinin denetimleri: açıklar önce, sonra yeniden eskiye. */
export function inspectionOptions(projects: readonly DrydockProject[]): InspectionOption[] {
  const all = projects.flatMap((project) =>
    project.inspections.map((inspection) => ({ inspection, project })),
  );
  const open = (o: InspectionOption) => (o.inspection.state === 'open' ? 0 : 1);
  return all.sort(
    (a, b) =>
      open(a) - open(b) ||
      b.inspection.openedOn.localeCompare(a.inspection.openedOn) ||
      b.inspection.inspectionNo.localeCompare(a.inspection.inspectionNo),
  );
}

/** İstenen denetim yoksa ilk seçenek (açık denetim önce). */
export function pickInspection(
  options: readonly InspectionOption[],
  wanted: string | null,
): string | null {
  if (wanted && options.some((o) => o.inspection.id === wanted)) return wanted;
  return options[0]?.inspection.id ?? null;
}
